import React, { useEffect, useRef, useState } from 'react';
import Modal from '../Modal';
import { CROP_PRESETS, cropPhoto, cropRect } from './crop';
import './PhotoCropper.css';

export default function PhotoCropper({ file, onCancel, onCrop }: {
  file: File;
  onCancel: () => void;
  onCrop: (file: File) => void;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [ratio, setRatio] = useState(CROP_PRESETS[0].ratio);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ distance: number; zoom: number; anchorX: number; anchorY: number } | null>(null);
  const drag = useRef<{ x: number; y: number; cx: number; cy: number; scale: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const photo = new Image();
    let active = true;
    photo.onload = () => {
      if (!active) return;
      setImage(photo);
      setCenter({ x: photo.naturalWidth / 2, y: photo.naturalHeight / 2 });
    };
    photo.onerror = () => { if (active) setError('Could not open this photo. Try a JPEG, PNG, or WebP.'); };
    photo.src = url;
    return () => { active = false; URL.revokeObjectURL(url); };
  }, [file]);

  const rect = image ? cropRect(image.naturalWidth, image.naturalHeight, ratio, zoom, center.x, center.y) : null;
  function move(x: number, y: number) {
    if (!image) return;
    const next = cropRect(image.naturalWidth, image.naturalHeight, ratio, zoom, x, y);
    setCenter({ x: next.x + next.width / 2, y: next.y + next.height / 2 });
  }
  const view = useRef({ image, ratio, zoom, rect, busy });
  view.current = { image, ratio, zoom, rect, busy };

  function zoomAt(nextZoom: number, clientX: number, clientY: number, anchor?: { x: number; y: number }) {
    const state = view.current;
    const bounds = frameRef.current?.getBoundingClientRect();
    if (!state.image || !state.rect || !bounds?.width || state.busy) return;
    const x = (clientX - bounds.left) / bounds.width;
    const y = (clientY - bounds.top) / bounds.height;
    const point = anchor || { x: state.rect.x + x * state.rect.width, y: state.rect.y + y * state.rect.height };
    const value = Math.max(1, Math.min(3, nextZoom));
    const size = cropRect(state.image.naturalWidth, state.image.naturalHeight, state.ratio, value, 0, 0);
    const next = cropRect(state.image.naturalWidth, state.image.naturalHeight, state.ratio, value,
      point.x + (0.5 - x) * size.width, point.y + (0.5 - y) * size.height);
    view.current = { ...state, zoom: value, rect: next };
    setZoom(value);
    setCenter({ x: next.x + next.width / 2, y: next.y + next.height / 2 });
  }

  // Trackpad pinch emits ctrl+wheel in Chromium; Safari emits gesture events.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    let safariZoom = 1;
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      zoomAt(view.current.zoom * Math.exp(-event.deltaY * 0.01), event.clientX, event.clientY);
    };
    const start = (event: Event) => { event.preventDefault(); safariZoom = view.current.zoom; };
    const change = (event: Event) => {
      event.preventDefault();
      const pinch = event as Event & { scale: number; clientX: number; clientY: number };
      if (Number.isFinite(pinch.scale)) zoomAt(safariZoom * pinch.scale, pinch.clientX, pinch.clientY);
    };
    const end = (event: Event) => event.preventDefault();
    frame.addEventListener('wheel', wheel, { passive: false });
    frame.addEventListener('gesturestart', start, { passive: false });
    frame.addEventListener('gesturechange', change, { passive: false });
    frame.addEventListener('gestureend', end, { passive: false });
    return () => {
      frame.removeEventListener('wheel', wheel);
      frame.removeEventListener('gesturestart', start);
      frame.removeEventListener('gesturechange', change);
      frame.removeEventListener('gestureend', end);
    };
  }, [image]);

  function beginGesture() {
    const points = Array.from(pointers.current.values());
    const current = view.current.rect;
    const bounds = frameRef.current?.getBoundingClientRect();
    drag.current = null; gesture.current = null;
    if (!current || !bounds?.width) return;
    if (points.length === 1) drag.current = { ...points[0], cx: current.x + current.width / 2,
      cy: current.y + current.height / 2, scale: current.width / bounds.width };
    if (points.length === 2) {
      const [a, b] = points;
      const distance = Math.hypot(b.x - a.x, b.y - a.y);
      if (distance > 0) gesture.current = { distance, zoom: view.current.zoom,
        anchorX: current.x + ((a.x + b.x) / 2 - bounds.left) / bounds.width * current.width,
        anchorY: current.y + ((a.y + b.y) / 2 - bounds.top) / bounds.height * current.height };
    }
  }
  function endPointer(event: React.PointerEvent<HTMLDivElement>) {
    if (!pointers.current.delete(event.pointerId)) return;
    beginGesture();
  }

  async function apply() {
    if (!image || !rect || busy) return;
    setBusy(true); setError('');
    try { onCrop(await cropPhoto(image, rect, file.name)); }
    catch (err) { setError((err as Error).message); setBusy(false); }
  }
  const cancel = () => { if (!busy) onCancel(); };
  return <Modal title="Crop photo" open onClose={cancel} className="modal--calendar" footer={<>
    <button type="button" className="btn btn--ghost" onClick={cancel} disabled={busy}>Cancel</button>
    <button type="button" className="btn btn--primary" onClick={apply} disabled={busy || !image}>{busy ? 'Cropping…' : 'Use photo'}</button>
  </>}>
    <div className="photo-cropper">
      <div className="crop-presets" role="group" aria-label="Photo proportions">
        {CROP_PRESETS.map((preset) => <button key={preset.ratio} type="button" className="btn btn--ghost btn--sm"
          aria-pressed={ratio === preset.ratio} disabled={busy} onClick={() => {
            setRatio(preset.ratio); setZoom(1);
            if (image) setCenter({ x: image.naturalWidth / 2, y: image.naturalHeight / 2 });
          }}>{preset.label}</button>)}
      </div>
      {error && <p className="banner banner--error" role="alert">{error}</p>}
      {!image && !error && <p className="muted" role="status">Loading photo…</p>}
      {image && rect && <>
        <p className="muted">Drag to frame. Pinch or spread two fingers to zoom.</p>
        <div ref={frameRef} className="crop-frame" style={{ aspectRatio: ratio }} tabIndex={0} role="group" aria-label="Photo framing"
          onKeyDown={(event) => {
            const step = rect.width / 30;
            const directions: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
            const delta = directions[event.key];
            if (delta && !busy) { event.preventDefault(); move(rect.x + rect.width / 2 + delta[0], rect.y + rect.height / 2 + delta[1]); }
          }}
          onPointerDown={(event) => {
            if (busy || (event.pointerType === 'mouse' && event.button !== 0)) return;
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
            beginGesture();
          }}
          onPointerMove={(event) => {
            if (!pointers.current.has(event.pointerId) || busy) return;
            pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
            const points = Array.from(pointers.current.values());
            const pinch = gesture.current;
            if (points.length === 2 && pinch) {
              const [a, b] = points;
              zoomAt(pinch.zoom * Math.hypot(b.x - a.x, b.y - a.y) / pinch.distance,
                (a.x + b.x) / 2, (a.y + b.y) / 2, { x: pinch.anchorX, y: pinch.anchorY });
            } else if (points.length === 1 && drag.current) {
              const start = drag.current;
              move(start.cx - (event.clientX - start.x) * start.scale, start.cy - (event.clientY - start.y) * start.scale);
            }
          }}
          onPointerUp={endPointer} onPointerCancel={endPointer} onLostPointerCapture={endPointer}>
          <img src={image.src} alt="Crop preview" draggable={false} style={{
            width: `${image.naturalWidth / rect.width * 100}%`, height: `${image.naturalHeight / rect.height * 100}%`,
            left: `${-rect.x / rect.width * 100}%`, top: `${-rect.y / rect.height * 100}%`,
          }} />
          <div className="crop-grid" aria-hidden="true" />
        </div>
        <label className="field crop-zoom">Zoom
          <input type="range" min="1" max="3" step="0.01" value={zoom} disabled={busy} onChange={(event) => {
            setCenter({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 });
            setZoom(Number(event.target.value));
          }} />
        </label>
      </>}
    </div>
  </Modal>;
}
